<svelte:options immutable={true} />

<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { pannable } from "../../actions/pannable.js";
  import { tapout } from "../../actions/tapout.js";
  import Toolbar from "../Toolbar.svelte";

  export let originalWidth;
  export let originalHeight;
  export let width;
  export let x;
  export let y;
  export let svgPath;
  export let opacity = 1;
  export let selected = false;
  export let hideBorders = false;

  const dispatch = createEventDispatcher();

  let wasEditing = false;
  $: {
    if (isEditing !== wasEditing) {
      if (isEditing) dispatch("select");
      else dispatch("blur");
      wasEditing = isEditing;
    }
  }

  const aspectRatio = originalWidth / originalHeight;

  let startPointerX = 0;
  let startPointerY = 0;
  let svgElement;

  let isEditing = false;
  let operation = "";
  let resizeDirection = "";

  let translateX = 0;
  let translateY = 0;
  let deltaWidth = 0;
  let draftOpacity = opacity;

  function initialize() {
    svgElement.setAttribute(
      "viewBox",
      `0 0 ${originalWidth} ${originalHeight}`,
    );
  }

  function onPanStart(event) {
    isEditing = true;
    startPointerX = event.detail.x;
    startPointerY = event.detail.y;

    if (event.detail.target === event.currentTarget) {
      operation = "move";
      return;
    }

    operation = "scale";
    resizeDirection = event.detail.target.dataset.direction || "";
  }

  function onPanMove(event) {
    const pointerDeltaX = (event.detail.x - startPointerX) / pageScale;
    const pointerDeltaY = (event.detail.y - startPointerY) / pageScale;

    if (operation === "move") {
      translateX = pointerDeltaX;
      translateY = pointerDeltaY;
      return;
    }

    if (operation !== "scale") return;

    if (resizeDirection === "left-top") {
      let d = Infinity;
      d = Math.min(pointerDeltaX, pointerDeltaY * aspectRatio);
      translateX = d;
      deltaWidth = -d;
      translateY = d / aspectRatio;
    }

    if (resizeDirection === "right-bottom") {
      let d = -Infinity;
      d = Math.max(pointerDeltaX, pointerDeltaY * aspectRatio);
      deltaWidth = d;
    }
  }

  function onPanEnd() {
    if (operation === "move") {
      dispatch("update", {
        x: x + translateX,
        y: y + translateY,
      });
    } else if (operation === "scale") {
      dispatch("update", {
        x: x + translateX,
        y: y + translateY,
        width: width + deltaWidth,
        scale: (width + deltaWidth) / originalWidth,
      });
    }

    operation = "";
    resizeDirection = "";
    translateX = 0;
    translateY = 0;
    deltaWidth = 0;
  }

  function onBlur() {
    isEditing = false;
  }

  function onDelete() {
    dispatch("delete");
  }

  onMount(initialize);
</script>

{#if isEditing}
  <Toolbar>
    <div class="flex items-center gap-3 px-1">
      <div class="flex items-center gap-2 border-l border-gray-200 px-3 ml-1">
        <span class="text-xs text-gray-500">透明度:</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          bind:value={draftOpacity}
          on:input={() => dispatch("update", { opacity: Number(draftOpacity) })}
          class="w-16 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          title="調整透明度"
        />
      </div>
    </div>
  </Toolbar>
{/if}

<div
  use:tapout
  on:tapout={onBlur}
  on:mousedown={() => (isEditing = true)}
  on:touchstart={() => (isEditing = true)}
  class="absolute left-0 top-0 select-none"
  class:outline={isEditing}
  class:outline-2={isEditing}
  class:outline-blue-500={isEditing}
  style="width: {width + deltaWidth}px; height: {(width + deltaWidth) /
    aspectRatio}px; transform: translate({x + translateX}px, {y +
    translateY}px); opacity: {draftOpacity};"
>
  <div
    use:pannable
    on:panstart={onPanStart}
    on:panmove={onPanMove}
    on:panend={onPanEnd}
    class="absolute w-full h-full cursor-grab border-gray-400"
    class:border={!hideBorders || selected || isEditing}
    class:border-dashed={!hideBorders || selected || isEditing}
    class:cursor-grabbing={operation === "move"}
    class:operation
  >
    {#if !hideBorders || selected || isEditing}
      <div
        data-direction="left-top"
        class="absolute left-0 top-0 w-10 h-10 bg-green-400 rounded-full
        cursor-nwse-resize transform -translate-x-1/2 -translate-y-1/2 md:scale-25"
      />
      <div
        data-direction="right-bottom"
        class="absolute right-0 bottom-0 w-10 h-10 bg-green-400 rounded-full
        cursor-nwse-resize transform translate-x-1/2 translate-y-1/2 md:scale-25"
      />
    {/if}
  </div>
  <svg bind:this={svgElement} width="100%" height="100%">
    <path
      stroke-width="5"
      stroke-linejoin="round"
      stroke-linecap="round"
      stroke="black"
      fill="none"
      d={svgPath}
    />
  </svg>
</div>

<style>
  .operation {
    background-color: rgba(0, 0, 0, 0.1);
  }
</style>
